import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router'
import type { Assignment, Submission } from '../../proto/qf/types_pb'
import { convertToBigInt, hasReviews, isManuallyGraded } from '../Helpers'
import { useActions, useAppState } from '../overmind'
import { CenteredMessage, KnownMessage } from './CenteredMessage'
import LabResultTable from "./LabResultTable"
import LogOutput from './LogOutput'
import { hasTestRun } from './submissions/SubmissionScore'
import ReviewResult from './ReviewResult'
import AssignmentFeedbackForm from './feedback/form/AssignmentFeedbackForm'


/** Lab displays a submission based on the /course/:id/lab/:lab route if the user is a student.
 *  If the user is a teacher, Lab displays the currently selected submission.
 */
const Lab = () => {
    const state = useAppState()
    const actions = useActions().global
    const { id, lab } = useParams()
    const courseID = id ?? ""
    const assignmentID = convertToBigInt(lab)
    const validAssignmentID = assignmentID > 0n
    const location = useLocation()
    const isGroupLab = location.pathname.includes("group-lab")

    useEffect(() => {
        if (!state.isTeacher) {
            if (validAssignmentID) {
                actions.setSelectedAssignmentID(assignmentID)
            } else {
                actions.setSelectedAssignmentID(-1n)
            }
        }
    }, [actions, assignmentID, state.isTeacher, validAssignmentID])

    const renderLab = () => {
        let submission: Submission | null
        let assignment: Assignment | null

        if (state.isTeacher) {
            // If used for grading purposes, retrieve the currently selected submission
            submission = state.selectedSubmission
            assignment = state.assignments[courseID].find(a => a.ID === submission?.AssignmentID) ?? null
        } else {
            // Retrieve the student's submission
            if (!validAssignmentID) {
                return <CenteredMessage message={KnownMessage.StudentNoAssignment} />
            }
            assignment = state.assignments[courseID]?.find(a => a.ID === assignmentID) ?? null
            if (!assignment) {
                return <CenteredMessage message={KnownMessage.StudentNoAssignment} />
            }
            const submissions = state.submissions.ForAssignment(assignment)
            if (submissions.length === 0) {
                return <CenteredMessage message={KnownMessage.StudentNoSubmission} />
            }

            const query = (s: Submission) => isGroupLab
                ? s.groupID > 0n
                : s.userID === state.self.ID && s.groupID === 0n

            submission = submissions.find(s => query(s)) ?? null
        }

        if (assignment && submission) {
            // Confirm both assignment and submission exists before attempting to render
            const review = hasReviews(submission) ? submission.reviews : []
            let buildLog: React.JSX.Element[] = []
            const buildLogRaw = submission.BuildInfo?.BuildLog
            if (buildLogRaw) {
                // using the index as the key is not ideal, but in this case it is acceptable
                // because the log lines are not expected to change unless a new submission is made
                // in which case the component will be re-rendered anyways
                // eslint-disable-next-line react/no-array-index-key
                buildLog = buildLogRaw.split("\n").map((logLine: string, idx: number) => <span key={idx}>{logLine}<br /></span>) // skipcq: JS-0437
            }

            return (
                <div key={submission.ID.toString()} className="mb-4">
                    {!state.isTeacher && submission.score > assignment.scoreLimit && (
                        <AssignmentFeedbackForm assignment={assignment} courseID={courseID} />
                    )}
                    <LabResultTable submission={submission} assignment={assignment} />

                    {isManuallyGraded(assignment.reviewers) && review.length > 0 ? <ReviewResult review={review[0]} /> : null}

                    {/* The build log is the rest of the run: each test's own
                        output is shown with that test. It opens by itself only
                        when no test recorded anything, which is the case for a
                        run that failed to build and for submissions recorded
                        before the output was attributed per test. */}
                    <LogOutput defaultOpen={!submission.Scores.some(hasTestRun)}>{buildLog}</LogOutput>
                </div>
            )
        }
        return <CenteredMessage message={state.isTeacher ? KnownMessage.TeacherNoSubmission : KnownMessage.StudentNoSubmission} />
    }

    return (
        <div className={state.isTeacher ? "" : "row"}>
            <div className={state.isTeacher ? "" : "col-md-9"}>
                {renderLab()}
            </div>
        </div>
    )
}

export default Lab
